import { PaidInstallmentOption } from '@/constants/payment';
import { getInstallments } from '@/services/checkout';
import { InstallmentsData, OfferType } from '@/types';
import { formatPrice } from '@/utils/format';
import { getRecurrencePeriodLabel } from '@/utils/payment';
import { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';

type CalculatedInstallmentOption = InstallmentsData & {
  label: string;
  total: number;
};

export type UseFetchCalculatedInstallmentsProps = {
  offerId: string;
  offerPaid: boolean;
  offerType: OfferType;
  price: number;
  recurrencePeriod?: number;
  onCalculate?: (calculatedInstallments: CalculatedInstallmentOption[]) => void;
};

export const useFetchCalculatedInstallments = ({
  offerId,
  offerPaid = false,
  offerType,
  price,
  recurrencePeriod,
  onCalculate = () => {}
}: UseFetchCalculatedInstallmentsProps) => {
  const getSubscriptionInstallmentLabel = (installment: string, value: number) => {
    return `${installment}x de ${formatPrice(value)} / ${getRecurrencePeriodLabel(recurrencePeriod || 30)}`;
  };

  const getRegularInstallmentLabel = (installment: string, value: number) => {
    return `${installment}x de ${formatPrice(value)}`;
  };

  const getInstallmentLabel = (installment: string, value: number) => {
    return offerType === 'subscription'
      ? getSubscriptionInstallmentLabel(installment, value)
      : getRegularInstallmentLabel(installment, value);
  };

  const { data: calculatedInstallments = [], isFetching: isLoadingInstallments } = useQuery({
    queryKey: ['installments', offerId, price],
    queryFn: () => {
      if (offerPaid || price === 0 || !offerId) {
        return [];
      }
      if (offerPaid) {
        return [PaidInstallmentOption];
      }
      return getInstallments(offerId, price);
    },
    initialData: [],
    enabled: price > 0 && !offerPaid,
    select: (data) =>
      data
        .sort((a, b) => Number(a.installment) - Number(b.installment))
        .map((installment) => ({
          ...installment,
          label: getInstallmentLabel(installment.installment, installment.value),
          total: Number(installment.value) * Number(installment.installment)
        }))
  });

  const getInstallmentOption = (installment: string) => {
    return calculatedInstallments.find((option) => String(option.value) === installment);
  };

  const maxInstallmentOption = useMemo(() => {
    if (!calculatedInstallments.length) {
      return null;
    }
    return calculatedInstallments[calculatedInstallments.length - 1];
  }, [calculatedInstallments]);

  useEffect(() => {
    onCalculate?.(calculatedInstallments);
  }, [calculatedInstallments]);

  return {
    calculatedInstallments,
    getInstallmentOption,
    maxInstallmentOption,
    isLoadingInstallments
  };
};
